'use client';

import { useEffect, useRef } from 'react';

import { Button } from '@paction-2.0/ui';

import { MissingRequirementsHint } from '@/components/MissingRequirementsHint';

import type { VerificationChannel } from './useContactVerification';
import styles from './verificationPanel.module.scss';

const CODE_LENGTH = 6;

type VerificationPanelProps = {
  channel: VerificationChannel;
  destination: string;
  code: string;
  isSending: boolean;
  isConfirming: boolean;
  error: string | null;
  resent: boolean;
  onCodeChange: (value: string) => void;
  onVerify: () => void;
  onResend: () => void;
  onCancel: () => void;
  altChannel: { channel: VerificationChannel; destination: string } | null;
  onSwitchChannel: (
    channel: VerificationChannel,
    destination: string
  ) => void | Promise<void>;
  purpose?: string;
};

const maskEmail = (email: string) => {
  const [name, domain] = email.split('@');
  if (!name || !domain) return email;
  const head = name.slice(0, 2);
  return `${head}${'•'.repeat(Math.max(1, name.length - 2))}@${domain}`;
};

export function VerificationPanel({
  channel,
  destination,
  code,
  isSending,
  isConfirming,
  error,
  resent,
  onCodeChange,
  onVerify,
  onResend,
  onCancel,
  altChannel,
  onSwitchChannel,
  purpose = 'join'
}: VerificationPanelProps) {
  const target =
    channel === 'phone' ? destination : maskEmail(destination.toLowerCase());

  const isCodeComplete = code.length === CODE_LENGTH;

  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from(
    { length: CODE_LENGTH },
    (_, index) => code[index] ?? ''
  );

  useEffect(() => {
    if (!code) {
      inputsRef.current[0]?.focus();
    }
  }, [code]);

  const handleDigitChange = (index: number, raw: string) => {
    const nextChar = raw.replace(/\D/g, '').slice(-1);
    const nextDigits = [...digits];
    nextDigits[index] = nextChar;
    onCodeChange(nextDigits.join(''));

    if (nextChar && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== 'Backspace' || digits[index] || index === 0) return;
    inputsRef.current[index - 1]?.focus();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    onCodeChange(pasted);
    inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  return (
    <div className={styles.panel} role="group" aria-labelledby="verify-heading">
      <div className={styles.heading} id="verify-heading">
        {channel === 'phone' ? 'Check your texts' : 'Check your email'}
      </div>
      <p className={styles.subtitle}>
        {isSending
          ? 'Sending your code…'
          : `We sent a 6-digit code to ${target}. Enter it below, then press Verify to ${purpose}.`}
      </p>
      {!isSending && channel === 'email' && (
        <p className={styles.subtitle}>
          It usually arrives within a minute. If you don&apos;t see it, check
          your junk or spam folder. A code from an earlier email in the last 10
          minutes also works.
        </p>
      )}

      <div
        className={styles.codeInputRow}
        role="group"
        aria-label="Verification code"
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            className={styles.codeInputBox}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(event) => handleDigitChange(index, event.target.value)}
            onKeyDown={(event) => handleDigitKeyDown(index, event)}
            onPaste={handlePaste}
            aria-label={`Verification code digit ${index + 1}`}
            autoFocus={index === 0}
            disabled={isConfirming}
          />
        ))}
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {resent && !error && <p className={styles.notice}>New code sent.</p>}
      {isConfirming && <p className={styles.notice}>Verifying…</p>}

      <Button
        type="button"
        variant="primary"
        onClick={onVerify}
        disabled={!isCodeComplete || isConfirming || isSending}
        aria-describedby={
          code.length > 0 && !isCodeComplete ? 'verify-code-hint' : undefined
        }
      >
        {isConfirming ? 'Verifying…' : 'Verify'}
      </Button>
      {code.length > 0 && !isCodeComplete && (
        <MissingRequirementsHint
          id="verify-code-hint"
          requirements={[`enter all ${CODE_LENGTH} digits of the code`]}
        />
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.link}
          onClick={onResend}
          disabled={isSending || isConfirming}
        >
          Resend code
        </button>
        {altChannel && (
          <button
            type="button"
            className={styles.link}
            onClick={() =>
              void onSwitchChannel(altChannel.channel, altChannel.destination)
            }
            disabled={isSending || isConfirming}
          >
            {altChannel.channel === 'email'
              ? 'Email me a code instead'
              : 'Text me a code instead'}
          </button>
        )}
        <Button variant="secondary" onClick={onCancel} disabled={isConfirming}>
          Back
        </Button>
      </div>
    </div>
  );
}
